import { _basePortalApi, Tag } from "common/state/portalApi/base";
import { CarbonCrop } from "protos/portal/carbon_crops";

// note: carbon crops is using Huma so we can't lean on protos

type ListCarbonCropsRequest = {
  limit?: number;
  pageToken?: string;
  search?: string;
  includeArchived?: boolean;
};

/** Cursor-paginated catalog search; the cursor travels as the RTK page param. */
type CarbonCropSearchRequest = {
  limit: number;
  search?: string;
};

type CustomerCarbonCropSearchRequest = CarbonCropSearchRequest & {
  customerUuid: string;
};

export type CustomerEnabledCarbonCrop = {
  uuid: string;
  name: string;
  translations: { language: string; translation: string }[];
  veselkaCropIds: string[];
};

type ListCustomerEnabledCarbonCropsResponse = {
  crops: CustomerEnabledCarbonCrop[];
  nextPageToken: string;
};

type ListCustomerCarbonCropStatusRequest = {
  customerUuid: string;
  limit?: number;
  pageToken?: string;
  search?: string;
  enabledOnly?: boolean;
};

export type AdminCarbonCrop = CarbonCrop & {
  veselkaCropIds: string[];
  archivedAt?: string;
};

export type ListAdminCarbonCropsResponse = {
  crops: AdminCarbonCrop[];
  nextPageToken: string;
};

export type UnassociatedVeselkaCrop = {
  id: string;
  commonName: string;
  description: string;
  archived: boolean;
};

export type ListUnassociatedVeselkaCropsResponse = {
  crops: UnassociatedVeselkaCrop[];
};

export type CarbonCropStatus = {
  uuid: string;
  name: string;
  translations: { language: string; translation: string }[];
  createdAt?: string;
  updatedAt?: string;
  enabled: boolean;
};

export type ListCustomerCarbonCropStatusResponse = {
  crops: CarbonCropStatus[];
  nextPageToken: string;
};

type SetCustomerCarbonCropsRequest = {
  customerUuid: string;
  cropIds: string[];
  enabled: boolean;
};

export type RobotCarbonCrop = CustomerEnabledCarbonCrop & {
  createdAt?: string;
  updatedAt?: string;
  pinnedModelId?: string;
};

type ListRobotCarbonCropsRequest = {
  serial: string;
  limit?: number;
  pageToken?: string;
  search?: string;
};

export type ListRobotCarbonCropsResponse = {
  crops: RobotCarbonCrop[];
  nextPageToken: string;
  recommendedModelId?: string;
};

type SetRobotCarbonCropPinnedModelRequest = {
  serial: string;
  id: string;
  pinnedModelId: string;
};

type UpdateCarbonCropRequest = {
  id: string;
  name: string;
  translations: { language: string; translation: string }[];
  veselkaCropIds: string[];
  archive?: boolean;
};

type ListUnassociatedVeselkaCropsRequest = {
  includeAssociatedWithCarbonCropId?: string;
};

export const carbonCropsApi = _basePortalApi.injectEndpoints({
  endpoints: (builder) => ({
    listCarbonCrops: builder.query<
      ListAdminCarbonCropsResponse,
      ListCarbonCropsRequest
    >({
      query: (params) => ({
        url: "carbon-crops",
        params,
      }),
      providesTags: (result) => [
        Tag.CARBON_CROP,
        ...(result?.crops ?? []).map((crop) => ({
          type: Tag.CARBON_CROP as const,
          id: crop.uuid,
        })),
      ],
    }),
    listCarbonCropsCursor: builder.infiniteQuery<
      ListAdminCarbonCropsResponse,
      CarbonCropSearchRequest,
      string | undefined
    >({
      query: ({ queryArg: { limit, search }, pageParam }) => ({
        url: "carbon-crops",
        params: {
          limit,
          ...(search ? { search } : {}),
          ...(pageParam ? { pageToken: pageParam } : {}),
        },
      }),
      infiniteQueryOptions: {
        initialPageParam: undefined,
        getNextPageParam: (lastPage) => lastPage.nextPageToken || undefined,
      },
      providesTags: [Tag.CARBON_CROP],
    }),
    getCarbonCrop: builder.query<AdminCarbonCrop, string>({
      query: (id) => `carbon-crops/${id}`,
      providesTags: (_result, _error, id) => [{ type: Tag.CARBON_CROP, id }],
    }),
    createCarbonCrop: builder.mutation<
      AdminCarbonCrop,
      { name: string; veselkaCropIds: string[] }
    >({
      query: ({ name, veselkaCropIds }) => ({
        url: "carbon-crops",
        method: "POST",
        body: { name, veselkaCropIds },
      }),
      invalidatesTags: (_result, error) =>
        error
          ? []
          : [Tag.CARBON_CROP, Tag.CARBON_CROP_CUSTOMER, Tag.CARBON_CROP_ROBOT],
    }),
    updateCarbonCrop: builder.mutation<
      AdminCarbonCrop,
      UpdateCarbonCropRequest
    >({
      query: ({ id, name, translations, veselkaCropIds, archive }) => ({
        url: `carbon-crops/${id}`,
        method: "PUT",
        body: {
          name,
          translations,
          veselkaCropIds,
          ...(archive === undefined ? {} : { archive }),
        },
      }),
      invalidatesTags: (_result, error, { id }) =>
        error
          ? []
          : [
              Tag.CARBON_CROP,
              Tag.CARBON_CROP_CUSTOMER,
              Tag.CARBON_CROP_ROBOT,
              { type: Tag.CARBON_CROP, id },
            ],
    }),
    listUnassociatedVeselkaCrops: builder.query<
      ListUnassociatedVeselkaCropsResponse,
      ListUnassociatedVeselkaCropsRequest
    >({
      query: (params) => ({
        url: "carbon-crops/unassociated-veselka-crops",
        params,
        timeout: 5000,
      }),
      providesTags: [Tag.CARBON_CROP],
    }),
    // Huma JSON; the proto ListCustomerCarbonCropsResponse omits veselkaCropIds,
    // so this reads the Huma body shape rather than going through fromJSON.
    listCustomerEnabledCarbonCropsCursor: builder.infiniteQuery<
      ListCustomerEnabledCarbonCropsResponse,
      CustomerCarbonCropSearchRequest,
      string | undefined
    >({
      query: ({ queryArg: { customerUuid, limit, search }, pageParam }) => ({
        url: `customers/${customerUuid}/carbon-crops`,
        params: {
          limit,
          ...(search ? { search } : {}),
          ...(pageParam ? { pageToken: pageParam } : {}),
        },
      }),
      infiniteQueryOptions: {
        initialPageParam: undefined,
        getNextPageParam: (lastPage) => lastPage.nextPageToken || undefined,
      },
      providesTags: (_result, _error, { customerUuid }) => [
        { type: Tag.CARBON_CROP_CUSTOMER, id: customerUuid },
      ],
    }),
    listCustomerCarbonCropStatus: builder.query<
      ListCustomerCarbonCropStatusResponse,
      ListCustomerCarbonCropStatusRequest
    >({
      query: ({ customerUuid, enabledOnly, ...params }) => ({
        url: `customers/${customerUuid}/carbon-crops/status`,
        params: {
          ...params,
          ...(enabledOnly ? { enabledOnly: true } : {}),
        },
      }),
      transformResponse: (response: unknown) =>
        response as ListCustomerCarbonCropStatusResponse,
      providesTags: (result, _error, { customerUuid }) => [
        { type: Tag.CARBON_CROP_CUSTOMER, id: customerUuid },
        ...(result?.crops ?? []).map((crop) => ({
          type: Tag.CARBON_CROP as const,
          id: crop.uuid,
        })),
      ],
    }),
    setCustomerCarbonCropsEnabled: builder.mutation<
      void,
      SetCustomerCarbonCropsRequest
    >({
      query: ({ customerUuid, cropIds, enabled }) => ({
        url: `customers/${customerUuid}/carbon-crops`,
        method: "PUT",
        body: { cropIds, enabled },
      }),
      invalidatesTags: (_result, error, { customerUuid }) =>
        error
          ? []
          : [
              { type: Tag.CARBON_CROP_CUSTOMER, id: customerUuid },
              Tag.CARBON_CROP_ROBOT,
            ],
    }),
    listRobotCarbonCrops: builder.query<
      ListRobotCarbonCropsResponse,
      ListRobotCarbonCropsRequest
    >({
      query: ({ serial, ...params }) => ({
        url: `robots/${serial}/carbon-crops`,
        params,
      }),
      providesTags: (_result, _error, { serial }) => [
        { type: Tag.CARBON_CROP_ROBOT, id: serial },
      ],
    }),
    setRobotCarbonCropPinnedModel: builder.mutation<
      void,
      SetRobotCarbonCropPinnedModelRequest
    >({
      query: ({ serial, id, pinnedModelId }) => ({
        url: `robots/${serial}/carbon-crops/${id}`,
        method: "PUT",
        body: { pinnedModelId },
      }),
      invalidatesTags: (_result, error, { serial }) =>
        error ? [] : [{ type: Tag.CARBON_CROP_ROBOT, id: serial }],
    }),
  }),
  overrideExisting: "throw",
});

export const carbonCropsEndpoints = carbonCropsApi.endpoints;
